<template>
  <div class="schedule-editor">
    <!-- Frequency selector -->
    <v-select
      :value="value.frequency"
      :items="freqItems"
      outlined
      rounded
      dense
      hide-details
      class="v-select-mini mb-2"
      @change="onFrequencyChange"
    />

    <!--
      NOTA: De momento solo se muestra el selector de frecuencia (arriba).
      Las opciones avanzadas (rangos de horas, día de la semana, día del mes)
      quedan ocultas mediante el flag `showScheduleDetails` (false) para
      reactivarlas en el futuro sin tener que reescribir el código.
    -->

    <!-- hourly / daily: time ranges -->
    <template
      v-if="
        showScheduleDetails &&
          (value.frequency === 'hourly' || value.frequency === 'daily')
      "
    >
      <div
        v-for="(range, idx) in activeRanges"
        :key="idx"
        class="d-flex align-center schedule-range-row"
        style="gap: 6px;"
      >
        <v-select
          :value="range.from"
          :items="hourItems"
          outlined
          rounded
          dense
          hide-details
          class="v-select-mini flex-grow-1"
          @change="onRangeChange(idx, 'from', $event)"
        />
        <span class="text-caption grey--text">–</span>
        <v-select
          :value="range.to"
          :items="hourItems"
          outlined
          rounded
          dense
          hide-details
          class="v-select-mini flex-grow-1"
          @change="onRangeChange(idx, 'to', $event)"
        />
        <v-btn
          icon
          x-small
          :disabled="activeRanges.length <= 1"
          @click="removeRange(idx)"
        >
          <v-icon size="14">mdi-close</v-icon>
        </v-btn>
      </div>
      <v-btn x-small text color="secondary" class="mt-1 px-0" @click="addRange">
        <v-icon size="14" class="mr-1">mdi-plus</v-icon>
        {{ $t("schedule_add_range") }}
      </v-btn>
    </template>

    <!-- weekly -->
    <template v-if="showScheduleDetails && value.frequency === 'weekly'">
      <div
        class="d-flex"
        style="gap: 4px; flex-wrap: wrap; margin-bottom: 6px;"
      >
        <v-btn
          v-for="wd in weekdays"
          :key="wd.value"
          x-small
          rounded
          :color="weeklyWeekday === wd.value ? 'secondary' : 'grey lighten-3'"
          :class="
            weeklyWeekday === wd.value
              ? 'white--text'
              : 'grey--text text--darken-1'
          "
          class="schedule-wd-btn"
          @click="setWeeklyWeekday(wd.value)"
        >
          {{ wd.short }}
        </v-btn>
      </div>
      <v-select
        :value="weeklyTime"
        :items="hourItems"
        outlined
        rounded
        dense
        hide-details
        class="v-select-mini"
        @change="setWeeklyTime"
      />
    </template>

    <!-- monthly -->
    <template v-if="showScheduleDetails && value.frequency === 'monthly'">
      <v-select
        :value="monthlyDay"
        :items="dayOfMonthItems"
        outlined
        rounded
        dense
        hide-details
        class="v-select-mini mb-2"
        @change="setMonthlyDay"
      />
      <v-select
        :value="monthlyTime"
        :items="hourItems"
        outlined
        rounded
        dense
        hide-details
        class="v-select-mini"
        @change="setMonthlyTime"
      />
    </template>
  </div>
</template>

<script>
export default {
  name: "a-pricing-strategy-schedule-editor",
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    // De momento solo mostramos el selector de frecuencia.
    // Pon a `true` para reactivar las opciones avanzadas (horas, días, etc.).
    showScheduleDetails: false
  }),
  computed: {
    freqItems() {
      return ["hourly", "daily", "weekly", "monthly"].map(v => ({
        text: this.$t("evaluation_frequency_" + v),
        value: v
      }));
    },
    hourItems() {
      return Array.from({ length: 24 }, (_, i) => {
        const h = String(i).padStart(2, "0") + ":00";
        return { text: h, value: h };
      });
    },
    weekdays() {
      return [
        { value: 1, short: this.$t("schedule_weekday_short_1") },
        { value: 2, short: this.$t("schedule_weekday_short_2") },
        { value: 3, short: this.$t("schedule_weekday_short_3") },
        { value: 4, short: this.$t("schedule_weekday_short_4") },
        { value: 5, short: this.$t("schedule_weekday_short_5") },
        { value: 6, short: this.$t("schedule_weekday_short_6") },
        { value: 7, short: this.$t("schedule_weekday_short_7") }
      ];
    },
    dayOfMonthItems() {
      return Array.from({ length: 28 }, (_, i) => ({
        text: String(i + 1),
        value: i + 1
      }));
    },
    // Shorthand getters for the active sub-objects
    activeRanges() {
      const freq = this.value.frequency;
      return (
        (this.value[freq] && this.value[freq].ranges) || [
          { from: "08:00", to: "20:00" }
        ]
      );
    },
    weeklyWeekday() {
      return (this.value.weekly && this.value.weekly.weekday) || 1;
    },
    weeklyTime() {
      return (this.value.weekly && this.value.weekly.time) || "09:00";
    },
    monthlyDay() {
      return (this.value.monthly && this.value.monthly.day_of_month) || 1;
    },
    monthlyTime() {
      return (this.value.monthly && this.value.monthly.time) || "09:00";
    }
  },
  methods: {
    emit(patch) {
      this.$emit("input", { ...this.value, ...patch });
    },
    onFrequencyChange(freq) {
      // Keep all sub-objects intact; just update frequency
      this.emit({ frequency: freq });
    },
    onRangeChange(idx, field, val) {
      const freq = this.value.frequency;
      const ranges = JSON.parse(JSON.stringify(this.activeRanges));
      ranges[idx] = { ...ranges[idx], [field]: val };
      this.emit({ [freq]: { ranges } });
    },
    addRange() {
      const freq = this.value.frequency;
      const ranges = JSON.parse(JSON.stringify(this.activeRanges));
      ranges.push({ from: "08:00", to: "20:00" });
      this.emit({ [freq]: { ranges } });
    },
    removeRange(idx) {
      const freq = this.value.frequency;
      const ranges = JSON.parse(JSON.stringify(this.activeRanges));
      ranges.splice(idx, 1);
      this.emit({ [freq]: { ranges } });
    },
    setWeeklyWeekday(wd) {
      this.emit({ weekly: { ...this.value.weekly, weekday: wd } });
    },
    setWeeklyTime(t) {
      this.emit({ weekly: { ...this.value.weekly, time: t } });
    },
    setMonthlyDay(d) {
      this.emit({ monthly: { ...this.value.monthly, day_of_month: d } });
    },
    setMonthlyTime(t) {
      this.emit({ monthly: { ...this.value.monthly, time: t } });
    }
  }
};
</script>

<style scoped>
.schedule-editor {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.schedule-range-row {
  align-items: center;
}
.schedule-wd-btn {
  min-width: 28px !important;
  padding: 0 6px !important;
  height: 24px !important;
}
</style>
